import * as THREE from 'three';
import { EventBus, EV } from './EventBus.js';
import { Time } from './Time.js';
import { Input } from './Input.js';
import { Ring, clamp } from '../util/math.js';

/**
 * Engine owns the frame. It knows nothing about guns, enemies or levels — it
 * holds the renderer/scene/camera, drives the fixed-step clock, and walks a
 * list of registered systems.
 *
 * A system is any object with an optional subset of:
 *   { name, priority, init(engine), fixedUpdate(dt, engine), update(dt, alpha, engine),
 *     lateUpdate(dt, engine), resize(w, h, engine), dispose() }
 *
 * Lower `priority` runs first. Ordering matters: movement (10) must resolve
 * before the camera rig (20) reads the player position, and the view model (30)
 * must run after the camera so it can parent to the final camera transform.
 */
export const PRIORITY = {
  INPUT: 0, AI: 5, MOVEMENT: 10, COMBAT: 15, CAMERA: 20, WEAPON: 30,
  FX: 40, WORLD: 45, AUDIO: 50, UI: 60, RENDER: 100,
};

/**
 * Orçamento de sombra por tier. Medido, não estimado — `tools/profile-gpu.mjs
 * --ablate ultra` mostrou a sombra do sol custando 13 dos 23 ms do quadro em
 * ultra, mais que a cadeia inteira de pós-processamento (7,4 ms) e quase quatro
 * vezes o passe de AO (3,9 ms). Era de longe o item mais caro do jogo.
 *
 * O culpado não é o mapa em si: é o desfoque do VSM. O three desfoca o mapa
 * inteiro em duas passadas de `blurSamples` taps cada, sobre a área TODA do
 * mapa, todo quadro. Em 4096 com 16 taps isso é 2 x 16,7 M texels x 16 buscas.
 * A varredura (`--shadow-sweep ultra`, vista courtyard) separou as duas causas:
 *
 *     4096/16  23,4 ms      2048/16  18,2 ms      1024/16  17,9 ms
 *     4096/8   19,3 ms      2048/8   16,2 ms      1024/8   16,1 ms
 *     4096/4   18,6 ms      2048/4   15,7 ms      1024/4   15,8 ms
 *
 * Abaixo de 2048 o ganho para de existir (o custo deixa de ser o desfoque e
 * passa a ser desenhar a geometria no mapa), então 2048 é o joelho da curva:
 * é o maior mapa que ainda é barato. 4096 pagava 7 ms por nitidez que o próprio
 * desfoque do VSM apaga em seguida — um raio de 4 texels em 4096 cobre a mesma
 * área de mundo que 2 texels em 2048.
 *
 * `radius` e `blurSamples` são o que distingue os tiers agora: ultra fica com
 * uma sombra mais fechada e mais bem amostrada que high, pelo mesmo preço de
 * memória. `radius` não custa nada — escala o deslocamento das buscas, não o
 * número delas.
 */
const SHADOW_TIERS = {
  low: { size: 1024, blur: 4, radius: 1.5 },
  high: { size: 2048, blur: 6, radius: 4 },
  ultra: { size: 2048, blur: 10, radius: 2.6 },
};

export class Engine {
  constructor({ container, quality = 'auto' } = {}) {
    this.container = container || document.getElementById('app');
    this.bus = new EventBus();
    this.time = new Time({ hz: 120 });
    this.systems = [];
    this._addCounter = 0;
    this.running = false;
    this.paused = false;
    this._disposers = [];

    this.scene = new THREE.Scene();
    this.scene.matrixWorldAutoUpdate = true;

    this.camera = new THREE.PerspectiveCamera(80, 1, 0.02, 600);
    this.camera.rotation.order = 'YXZ'; // yaw then pitch — no roll-induced gimbal
    this.scene.add(this.camera);

    this.renderer = this._createRenderer();
    this.input = new Input(this.bus, this.renderer.domElement);

    // Shared registry so systems can find each other without hard imports.
    this.services = new Map();

    this.quality = this._resolveQuality(quality);
    this._bootQuality = this.quality;
    this.gpuTimes = new Ring(120);
    this._resolutionScale = 1;
    this._targetFrameMs = 1000 / 60;

    // Dynamic resolution state. The controller keeps its own short window
    // instead of reading time.ms99 — see _adaptResolution for why.
    this.dynamicResolution = true;
    this._resWindow = [];
    this._resCooldown = 0;
    this._resBounds = { min: 0.6, max: 1 };

    // Optional frame cap. 0 = uncapped (rAF decides).
    this._frameCapMs = 0;
    this._nextFrameAt = 0;

    // Lights whose shadow this engine switched off because the active shadow
    // map type cannot render them; remembered so a later type change can put
    // them back rather than leaving the level permanently darker.
    this._shadowDisabled = new Set();
    this._shadowBase = new WeakMap();

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this._onResize);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
    });

    this._loop = this._loop.bind(this);
  }

  _createRenderer() {
    const canvas = document.createElement('canvas');
    this.container.appendChild(canvas);
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,          // post-process AA handles this; MSAA + deferred FX is wasted fill
      alpha: false,
      stencil: false,
      depth: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true, // needed so the screenshot harness can read pixels
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.AgXToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    renderer.shadowMap.autoUpdate = true;
    renderer.autoClear = true;
    renderer.info.autoReset = true;
    return renderer;
  }

  /** Coarse GPU tier guess. Systems read `engine.quality` to size their budgets. */
  _resolveQuality(pref) {
    if (pref !== 'auto') return pref;
    const gl = this.renderer.getContext();
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const gpu = (dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '') || '';
    const mem = navigator.deviceMemory || 8;
    const cores = navigator.hardwareConcurrency || 4;
    const weak = /swiftshader|llvmpipe|software|intel.*(hd|uhd) graphics [45]/i.test(gpu);
    if (weak || mem <= 4 || cores <= 4) return 'low';
    if (/apple m[1-9]|rtx (30|40|50)|radeon rx (6|7|9)/i.test(gpu) || cores >= 10) return 'ultra';
    return 'high';
  }

  provide(name, obj) { this.services.set(name, obj); return obj; }
  get(name) { return this.services.get(name); }

  add(system) {
    system.priority = system.priority ?? PRIORITY.WORLD;
    // Registration order is the dependency order; priority is only the per-frame
    // update order. Keeping both means a system can run its update early while
    // still being constructed after whatever it depends on.
    system._addIndex = this._addCounter++;
    this.systems.push(system);
    this.systems.sort((a, b) => a.priority - b.priority);
    if (this.running && system.init) system.init(this);
    return system;
  }

  async init() {
    // Init in registration order, NOT priority order. Player has a lower
    // priority number than Level because it must move before the world updates,
    // but it needs level.colliders at init — initialising by priority handed it
    // an undefined level and it fell through the floor with no collision at all.
    const initOrder = this.systems.slice().sort((a, b) => a._addIndex - b._addIndex);
    for (const s of initOrder) {
      if (s.init) {
        const r = s.init(this);
        if (r && typeof r.then === 'function') await r;
      }
    }
    this.resize();
    // After every system has built its lights, not before.
    this._applyShadowQuality(this.quality);
    this.bus.emit(EV.READY, {});
  }

  // ---------------------------------------------------------------------------
  // Runtime quality
  //
  // Everything below can be changed while the game is running, without a
  // reload. `engine.quality` used to be decided once at boot and never looked
  // at again, which meant a player on a weak machine had no way down.
  // The UI layer drives these; see the header of src/render/PostFX.js for the
  // full menu-facing surface.
  // ---------------------------------------------------------------------------

  /**
   * Switch quality tier at runtime. Emits EV.QUALITY_CHANGED so every system
   * that sized a budget off `engine.quality` can resize it; PostFX already
   * listens and reconfigures its pass chain.
   */
  setQuality(q) {
    if (q !== 'low' && q !== 'high' && q !== 'ultra') {
      console.warn(`[engine] qualidade desconhecida: ${q}`);
      return this.quality;
    }
    if (q === this.quality) return q;
    this.quality = q;

    // A weak machine should not be asked to run at native resolution while it
    // is being told it is weak; a strong one gets the ceiling back.
    this._resBounds = q === 'low' ? { min: 0.5, max: 0.85 }
      : q === 'high' ? { min: 0.6, max: 1 }
        : { min: 0.75, max: 1 };
    this._resolutionScale = clamp(this._resolutionScale, this._resBounds.min, this._resBounds.max);
    this._resWindow.length = 0;
    this._resCooldown = 60;

    this._applyShadowQuality(q);
    this.resize();
    this.bus.emit(EV.QUALITY_CHANGED, { quality: q });
    return q;
  }

  /**
   * Shadow map budget per tier, plus the type. Point lights are the reason the
   * type matters: VSM has no cube-map path in three, so WebGLShadowMap logs a
   * warning and skips that light's shadow map entirely — every frame, once per
   * light. The six local lights in the level have therefore never cast a
   * shadow; all they ever produced was ~80 console warnings a second. Clearing
   * castShadow costs no pixel and silences the storm, and the flag is
   * remembered so switching to a type that CAN do point lights restores it.
   */
  _applyShadowQuality(q) {
    const renderer = this.renderer;
    // PCF at low: VSM spends an extra blur pass per shadow map, which is the
    // wrong thing to be paying for on the machine that asked for low.
    const type = q === 'low' ? THREE.PCFShadowMap : THREE.VSMShadowMap;
    renderer.shadowMap.type = type;
    // A point light's shadow is a cube map: six renders of the scene per light,
    // per frame. So it comes back only when the type can actually draw one AND
    // the tier can pay for it — handing two cube maps to the machine that just
    // asked for 'low' would make the cheap setting the expensive one.
    const canPoint = type !== THREE.VSMShadowMap && q === 'ultra';
    const tier = SHADOW_TIERS[q] || SHADOW_TIERS.high;
    const target = tier.size;
    // O denominador tem que ser o mesmo do numerador: as luzes locais guardam o
    // tamanho com que NASCERAM (Lighting/Level dimensionam por engine.quality no
    // init), então a razão só faz sentido contra o tamanho de boot da MESMA
    // tabela. Ler 4096 aqui e 2048 ali multiplicaria toda luz local por 0,5 sem
    // que ninguém tivesse pedido.
    const boot = (SHADOW_TIERS[this._bootQuality] || SHADOW_TIERS.high).size;
    const factor = target / boot;

    this.scene.traverse((o) => {
      if (!o.isLight || !o.shadow) return;
      if (o.isPointLight && !canPoint) {
        if (o.castShadow) { o.castShadow = false; this._shadowDisabled.add(o); }
      } else if (this._shadowDisabled.has(o)) {
        o.castShadow = true;
        this._shadowDisabled.delete(o);
      }

      // O desfoque do VSM é onde o dinheiro está: custo linear em blurSamples,
      // sobre a área INTEIRA do mapa, duas vezes por quadro. O three recompila
      // os dois materiais de desfoque sozinho quando este número muda, então dá
      // para mexer com o jogo rodando — custa um engasgo, uma vez, no menu.
      // Luzes locais nunca precisam do mesmo cuidado do sol: cobrem poucos
      // metros, então 4 taps já as deixam macias.
      if (type === THREE.VSMShadowMap) {
        o.shadow.blurSamples = o.isDirectionalLight ? tier.blur : Math.min(tier.blur, 4);
        // O raio é medido em texels do mapa. Mantê-lo fixo enquanto o mapa
        // encolhe alarga a penumbra em metros de mundo e borra a sombra até
        // virar mancha; a tabela já traz o par casado.
        if (o.isDirectionalLight) o.shadow.radius = tier.radius;
      }

      let base = this._shadowBase.get(o);
      if (!base) { base = { x: o.shadow.mapSize.x, y: o.shadow.mapSize.y }; this._shadowBase.set(o, base); }
      // The sun gets the absolute budget; anything else keeps whatever ratio to
      // it its own system chose, so a 512 spot does not jump to 4096.
      const w = o.isDirectionalLight ? target : Math.max(256, Math.round(base.x * factor));
      const h = o.isDirectionalLight ? target : Math.max(256, Math.round(base.y * factor));
      if (o.shadow.mapSize.x !== w || o.shadow.mapSize.y !== h) {
        o.shadow.mapSize.set(w, h);
        // three only allocates the map when it is null, so free it to force a
        // rebuild at the new size on the next shadow pass.
        if (o.shadow.map) { o.shadow.map.dispose(); o.shadow.map = null; }
      }
      o.shadow.needsUpdate = true;
    });
  }

  /** Fixed internal resolution multiplier. Turns the automatic one off. */
  setResolutionScale(s) {
    this.dynamicResolution = false;
    this._resolutionScale = clamp(s, 0.4, 1);
    this.resize();
    return this._resolutionScale;
  }

  /** Hand the internal resolution back to the automatic controller. */
  setDynamicResolution(on) {
    this.dynamicResolution = !!on;
    this._resWindow.length = 0;
    this._resCooldown = 60;
  }

  /**
   * Optional frame cap, in fps. 0 or falsy means uncapped. Useful on a laptop
   * where an uncapped loop is only a way of turning battery into fan noise, and
   * as the honest fix for a machine that renders 45 fps unevenly — 30 locked
   * reads smoother than 45 that stutters.
   */
  setFpsCap(fps) {
    const n = Number(fps) || 0;
    // Devolve o limite EFETIVO, não o pedido: 15 fps é o piso e 480 o teto, e
    // um menu que pediu 10 precisa saber que recebeu 15 para mostrar isso.
    const eff = n > 0 ? clamp(n, 15, 480) : 0;
    this._frameCapMs = eff > 0 ? 1000 / eff : 0;
    this._nextFrameAt = 0;
    // The resolution controller aims at whatever the cap is; targeting 60 while
    // capped at 30 would read every frame as comfortably fast and creep the
    // resolution up until the cap could no longer be held.
    this._targetFrameMs = this._frameCapMs > 0 ? this._frameCapMs : 1000 / 60;
    this._resWindow.length = 0;
    return eff;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._raf = requestAnimationFrame(this._loop);
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.input.exitLock();
    this.bus.emit(EV.PAUSE, {});
  }

  resume() {
    if (!this.paused) return;
    this.paused = false;
    // Reset the clock so the paused wall-time does not arrive as one huge dt.
    this.time._started = false;
    this.bus.emit(EV.RESUME, {});
  }

  resize() {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality === 'low' ? 1 : 2);
    this.renderer.setPixelRatio(dpr * this._resolutionScale);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // Hip-fire FOV is vertical-locked so ultrawide players see more horizontally
    // rather than a stretched vertical, matching how shooters ship it.
    this.camera.updateProjectionMatrix();
    for (const s of this.systems) if (s.resize) s.resize(w, h, this);
    this.bus.emit(EV.RESIZE, { width: w, height: h, dpr });
  }

  /**
   * Dynamic resolution: if we are consistently missing the frame budget, drop
   * internal resolution before we start dropping effects.
   *
   * The controller keeps its own window and clears it after every decision, so
   * each frame time is spent exactly once. The previous version read
   * `time.ms99`, a 99th percentile over a 180-frame ring — which at 60 fps is
   * effectively "the second-worst frame in the last three seconds". It then
   * acted every 60 frames, so one shader-compile hitch stayed in the window
   * long enough to be counted three separate times and cost three separate
   * steps down. Measured at boot, that is exactly what happened: 1 → 0.9 → 0.8
   * → 0.7 → 0.6 on frames 120/180/240/300, straight to the floor, one step per
   * second, from a hitch that had ended seconds earlier. Every one of those
   * steps calls resize(), which rebuilds every render target in the composer
   * mid-flight. That staircase is the "flicker just before the match starts".
   *
   * It also now judges by the MEDIAN, not a high percentile. Resolution should
   * track sustained cost; a hitch is a hitch and lowering resolution does not
   * bring it back.
   */
  _adaptResolution() {
    if (!this.dynamicResolution) return;
    // Shader compilation dominates the first couple of seconds and is not a
    // measurement of how fast this machine renders the game.
    if (this.time.elapsed < 2.5) return;

    if (this._resCooldown > 0) { this._resCooldown--; return; }

    const ms = this.time.rawDt * 1000;
    // A tab restore or a GC storm is not evidence about resolution.
    if (ms > 0 && ms < 250) this._resWindow.push(ms);
    if (this._resWindow.length < 45) return;

    this._resWindow.sort((a, b) => a - b);
    const median = this._resWindow[this._resWindow.length >> 1];
    this._resWindow.length = 0;

    const { min, max } = this._resBounds;
    const prev = this._resolutionScale;
    if (median > this._targetFrameMs * 1.25) this._resolutionScale = clamp(prev - 0.1, min, max);
    else if (median < this._targetFrameMs * 0.7) this._resolutionScale = clamp(prev + 0.05, min, max);

    if (Math.abs(prev - this._resolutionScale) > 0.001) {
      this.resize();
      // Let the new resolution actually be measured before judging it again.
      this._resCooldown = 45;
    }
  }

  _loop(ts) {
    this._raf = requestAnimationFrame(this._loop);
    if (this.paused) { this.time._started = false; return; }

    // Frame cap. Bailing out before time.begin() means the skipped interval is
    // still counted — the next frame simply gets the full delta — so the
    // simulation runs at the same speed, it just steps less often.
    if (this._frameCapMs > 0) {
      if (ts < this._nextFrameAt - 0.6) return;
      // Anchored to the previous deadline rather than to now, so a frame that
      // arrives late does not push every later one late with it; clamped to now
      // so a long stall cannot leave a backlog of deadlines in the past.
      this._nextFrameAt = Math.max(ts, this._nextFrameAt + this._frameCapMs);
    }

    const steps = this.time.begin(ts);
    const { dt, alpha, fixedDt } = this.time;

    this.input.beginFrame();

    for (let i = 0; i < steps; i++) {
      for (const s of this.systems) if (s.fixedUpdate) s.fixedUpdate(fixedDt, this);
    }
    for (const s of this.systems) if (s.update) s.update(dt, alpha, this);
    for (const s of this.systems) if (s.lateUpdate) s.lateUpdate(dt, this);

    this.input.endFrame();

    const composer = this.get('postfx');
    if (composer && composer.enabled !== false) composer.render(dt);
    else this.renderer.render(this.scene, this.camera);

    this._adaptResolution();
  }

  dispose() {
    cancelAnimationFrame(this._raf);
    this.running = false;
    window.removeEventListener('resize', this._onResize);
    for (const s of this.systems) if (s.dispose) s.dispose();
    this.input.dispose();
    this.renderer.dispose();
    this.bus.clear();
  }
}
